import { z } from 'zod';
import { AddressSchema } from '../../../backend_config.types.js';
import { PhoneNumberSchema } from '../../common.schemas.js';

import type {
    DealertrackDocumentType,
    DealertrackArStatementCustomersTransactionDetails,
    DealertrackDealTransactionExtension,
    DealertrackLeasingTransactionExtension,
    DealertrackPartsLineItem,
    DealertrackPartsTotals,
    DealertrackServiceBillingTotals,
    DealertrackServiceTotalsByPillar,
    DealertrackTransactionDetails,
} from '../../../types/ipw/dealertrack/dealertrack-transaction-details.js';
import { DealertrackDocumentTypeValues } from '../../../types/ipw/dealertrack/dealertrack-transaction-details.js';
import { ThirdPartySystemId } from '../../../types/third-party-system.enums.js';
import { TransactionDetailsTypeId } from '../../../types/transaction-details.enums.js';

export const DealertrackDocumentTypeSchema: z.ZodType<DealertrackDocumentType> = z.enum(
    DealertrackDocumentTypeValues
);

export const DealertrackPartsLineItemSchema: z.ZodType<DealertrackPartsLineItem> = z.object({
    optionCode: z.string().optional(),
    partNumber: z.string().optional(),
    description: z.string().optional(),
    binShelf: z.string().optional(),
    quantity: z.number().optional(),
    available: z.number().optional(),
    listPrice: z.number().optional(),
    netPrice: z.number().optional(),
    extendedPrice: z.number().optional(),
});

export const DealertrackPartsTotalsSchema: z.ZodType<DealertrackPartsTotals> = z.object({
    freight: z.number().optional(),
    fees: z.number().optional(),
    partsSubtotal: z.number().optional(),
    taxes: z.number().optional(),
    deposit: z.number().optional(),
    total: z.number().optional(),
});

export const DealertrackPartsTransactionExtensionSchema =
    z.object({
        partsInvoiceNumber: z.string().optional(),
        closeDate: z.string().optional(),
        paymentMethod: z.string().optional(),
        controlReference: z.string().optional(),
        accountCardLast4: z.string().optional(),
        counterperson: z.string().optional(),
        status: z.string().optional(),
        transactionType: z.string().optional(),
        customerAccountNumber: z.string().optional(),
        mainPhone: PhoneNumberSchema.optional(),
        poNumber: z.string().optional(),
        priceLevel: z.string().optional(),
        saleType: z.string().optional(),
        totals: DealertrackPartsTotalsSchema.optional(),
        lineItems: z.array(DealertrackPartsLineItemSchema).optional(),
    });

export const DealertrackServiceTotalsByPillarSchema: z.ZodType<DealertrackServiceTotalsByPillar> =
    z.object({
        labor: z.number().optional(),
        parts: z.number().optional(),
        deductible: z.number().optional(),
        sublet: z.number().optional(),
        shopSupplies: z.number().optional(),
        paintMaterials: z.number().optional(),
        miscCharges: z.number().optional(),
        totalCharges: z.number().optional(),
        salesTax: z.number().optional(),
        totalDue: z.number().optional(),
    });

export const DealertrackServiceBillingTotalsSchema: z.ZodType<DealertrackServiceBillingTotals> =
    z.object({
        customerPay: DealertrackServiceTotalsByPillarSchema.optional(),
        warranty: DealertrackServiceTotalsByPillarSchema.optional(),
        internal: DealertrackServiceTotalsByPillarSchema.optional(),
        serviceContract: DealertrackServiceTotalsByPillarSchema.optional(),
    });

export const DealertrackServiceTransactionExtensionSchema =
    z.object({
        closeDate: z.string().optional(),
        paymentMethod: z.string().optional(),
        controlReference: z.string().optional(),
        accountCardLast4: z.string().optional(),
        roNumber: z.string().optional(),
        status: z.string().optional(),
        vehicleDescription: z.string().optional(),
        vin: z.string().optional(),
        tagNumber: z.string().optional(),
        mileage: z.number().optional(),
        serviceWriter: z.string().optional(),
        dateOpened: z.string().optional(),
        serviceGroup: z.string().optional(),
        mainPhone: PhoneNumberSchema.optional(),
        billingTotalsByPillar: DealertrackServiceBillingTotalsSchema.optional(),
    });


export const DealertrackDealTransactionExtensionSchema = z.object({
        homePhone: PhoneNumberSchema.optional(),
        cellPhone: PhoneNumberSchema.optional(),
        email: z.string().optional(),
        county: z.string().optional(),
        stockNumber: z.string().optional(),
        vehicleDescription: z.string().optional(),
        vin: z.string().optional(),
        odometer: z.number().optional(),
        tradeDescription: z.string().optional(),
        msrp: z.number().optional(),
        price: z.number().optional(),
        downPayment: z.number().optional(),
        tradeAllowance: z.number().optional(),
        payoff: z.number().optional(),
        rebate: z.number().optional(),
        apr: z.number().optional(),
        termMonths: z.number().optional(),
        paymentAmount: z.number().optional(),
        paymentFrequency: z.string().optional(),
        saleDate: z.string().optional(),
        deliverDate: z.string().optional(),
        firstPaymentDate: z.string().optional(),
        amountOptions: z.number().optional(),
        serviceContractAmount: z.number().optional(),
        insuranceAmount: z.number().optional(),
        accessoriesAmount: z.number().optional(),
        lender: z.string().optional(),
        dealTax: z.number().optional(),
        taxGroup: z.string().optional(),
        salespersonPrimary: z.string().optional(),
        salespersonSecondary: z.string().optional(),
        financeManager: z.string().optional(),
        salesManager: z.string().optional(),
    }) satisfies z.ZodType<DealertrackDealTransactionExtension>;

export const DealertrackLeasingTransactionExtensionSchema = z.object({
    homePhone: PhoneNumberSchema.optional(),
    cellPhone: PhoneNumberSchema.optional(),
    email: z.string().optional(),
    county: z.string().optional(),
    stockNumber: z.string().optional(),
    dealStatus: z.string().optional(),
    vehicleDescription: z.string().optional(),
    vin: z.string().optional(),
    odometer: z.number().optional(),
    tradeDescription: z.string().optional(),
    msrp: z.number().optional(),
    price: z.number().optional(),
    tradeAllowance: z.number().optional(),
    payoff: z.number().optional(),
    rebate: z.number().optional(),
    residualPercentage: z.number().optional(),
    apr: z.number().optional(),
    moneyFactor: z.number().optional(),
    termMonths: z.number().optional(),
    cashReceived: z.number().optional(),
    upfrontAmount: z.number().optional(),
    cashCapReduction: z.number().optional(),
    saleDate: z.string().optional(),
    deliverDate: z.string().optional(),
    secondPaymentDate: z.string().optional(),
    amountOptions: z.number().optional(),
    insuranceAmount: z.number().optional(),
    capReductionAmount: z.number().optional(),
    accessoriesAmount: z.number().optional(),
    serviceContractAmount: z.number().optional(),
    lessor: z.string().optional(),
    acquisitionFee: z.number().optional(),
    leaseTax: z.number().optional(),
    cashRequired: z.number().optional(),
    taxGroup: z.string().optional(),
    securityDeposit: z.number().optional(),
    feeAmount: z.number().optional(),
    functionCode: z.string().optional(),
    permitNumberExpiration: z.string().optional(),
    annualMiles: z.number().optional(),
    pdi: z.string().optional(),
    profitCode: z.string().optional(),
    salespersonPrimary: z.string().optional(),
    salespersonSecondary: z.string().optional(),
    financeManager: z.string().optional(),
    salesManager: z.string().optional(),
    paymentAmount: z.number().optional(),
    paymentFrequency: z.string().optional(),
}) satisfies z.ZodType<DealertrackLeasingTransactionExtension>;

const DealertrackBaseTransactionDetailsShape = {
    amount: z.number().positive(),
    detectedAmount: z.number().positive().optional(),
    tax: z.number().optional(),
    fee: z.number().optional(),
    surcharge: z.number().optional(),
    currency: z.string().min(1),
    merchant_transaction_id: z.string().optional(),
    customerName: z.string().optional(),
    mainPhone: PhoneNumberSchema.optional(),
    otherPhones: z.array(PhoneNumberSchema).default([]),
    mainEmail: z.string().email().optional(),
    address: AddressSchema.optional(),
    description: z.string().optional(),
    notes: z.string().optional(),
    ipw_customer_id: z.string().optional(),
    ipw_system_id: z.literal(ThirdPartySystemId.DEALERTRACK),
};

export const DealertrackBaseTransactionDetailsSchema: z.ZodObject<
    typeof DealertrackBaseTransactionDetailsShape
> = z.object(DealertrackBaseTransactionDetailsShape);

export const DealertrackDealBaseTransactionDetailsSchema: z.ZodObject<
    Omit<typeof DealertrackBaseTransactionDetailsShape, 'tax'>
> = DealertrackBaseTransactionDetailsSchema.omit({ tax: true });

export const DealertrackPartsTransactionDetailsV1Schema =
    DealertrackBaseTransactionDetailsSchema.extend({
        transaction_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_PARTS),
        transaction_details_type_version: z.literal(1),
        // transaction_details_payload: DealertrackPartsTransactionExtensionSchema,
        dealertrack_screen: z.string().optional(),
    }).merge(DealertrackPartsTransactionExtensionSchema);

export const DealertrackServiceTransactionDetailsV1Schema =
    DealertrackBaseTransactionDetailsSchema.extend({
        transaction_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_SERVICE),
        transaction_details_type_version: z.literal(1),
        // transaction_details_payload: DealertrackServiceTransactionExtensionSchema,
        dealertrack_screen: z.string().optional(),
    }).merge(DealertrackServiceTransactionExtensionSchema);

export const DealertrackDealTransactionDetailsV1Schema =
    DealertrackDealBaseTransactionDetailsSchema.extend({
        transaction_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_DEAL),
        transaction_details_type_version: z.literal(1),
        // transaction_details_payload: DealertrackDealTransactionExtensionSchema,
        dealertrack_screen: z.string().optional(),
    }).merge(DealertrackDealTransactionExtensionSchema);

export const DealertrackLeasingTransactionDetailsV1Schema =
    DealertrackDealBaseTransactionDetailsSchema.extend({
        transaction_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_LEASING),
        transaction_details_type_version: z.literal(1),
        dealertrack_screen: z.string().optional(),
    }).merge(DealertrackLeasingTransactionExtensionSchema);

const DealertrackArStatementCustomersTransactionDetailsV1Shape = {
    transaction_details_type_id: z.literal(
        TransactionDetailsTypeId.DEALERTRACK_AR_STATEMENT_CUSTOMERS
    ),
    transaction_details_type_version: z.literal(1),
    dealertrack_screen: z.string().optional(),
};

export const DealertrackArStatementCustomersTransactionDetailsV1Schema: z.ZodObject<
    typeof DealertrackBaseTransactionDetailsShape &
        typeof DealertrackArStatementCustomersTransactionDetailsV1Shape
> = DealertrackBaseTransactionDetailsSchema.extend(
    DealertrackArStatementCustomersTransactionDetailsV1Shape
) satisfies z.ZodType<DealertrackArStatementCustomersTransactionDetails>;

export const DealertrackTransactionDetailsSchema: z.ZodType<DealertrackTransactionDetails> =
    z.discriminatedUnion('transaction_details_type_id', [
        DealertrackPartsTransactionDetailsV1Schema,
        DealertrackServiceTransactionDetailsV1Schema,
        DealertrackDealTransactionDetailsV1Schema,
        DealertrackLeasingTransactionDetailsV1Schema,
        DealertrackArStatementCustomersTransactionDetailsV1Schema,
    ]);
